import { useState, useEffect, useRef } from 'react';
import { useAccount, useConfig } from 'wagmi';
import { formatUnits, parseAbi, type Address } from 'viem';
import { getBalance, multicall, readContract } from 'viem/actions';
import { mainnet, base, optimism, arbitrum, polygon, bsc, avalanche } from 'wagmi/chains';
import { ink, monad } from '../utils/chains';
import { getRelayCurrencies } from '../utils/relayProtocol';
import { getAddressTokenBalances, BlockscoutTokenBalance } from '../utils/blockscoutApi';
import { supabase } from '../utils/supabase';

export interface TokenBalance {
  chainId: number;
  chainName: string;
  tokenAddress: Address | null; // null for native currency
  symbol: string;
  name: string;
  balance: string; // Formatted balance
  balanceRaw: bigint; // Raw balance in wei
  decimals: number;
  logo?: string;
  priceUSD?: number;
  valueUSD?: number;
}

// Define supported chains
const SUPPORTED_CHAINS = [
  { chain: ink, name: 'Ink', nativeSymbol: 'ETH', logo: 'https://icons.llamao.fi/icons/chains/rsz_ink.jpg' },
  { chain: mainnet, name: 'Ethereum', nativeSymbol: 'ETH', logo: 'https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg' },
  { chain: base, name: 'Base', nativeSymbol: 'ETH', logo: 'https://icons.llamao.fi/icons/chains/rsz_base.jpg' },
  { chain: optimism, name: 'Optimism', nativeSymbol: 'ETH', logo: 'https://icons.llamao.fi/icons/chains/rsz_optimism.jpg' },
  { chain: arbitrum, name: 'Arbitrum', nativeSymbol: 'ETH', logo: 'https://icons.llamao.fi/icons/chains/rsz_arbitrum.jpg' },
  { chain: polygon, name: 'Polygon', nativeSymbol: 'POL', logo: 'https://icons.llamao.fi/icons/chains/rsz_polygon.jpg' },
  { chain: bsc, name: 'BSC', nativeSymbol: 'BNB', logo: 'https://icons.llamao.fi/icons/chains/rsz_binance.jpg' },
  { chain: avalanche, name: 'Avalanche', nativeSymbol: 'AVAX', logo: 'https://icons.llamao.fi/icons/chains/rsz_avalanche.jpg' },
  { chain: monad, name: 'Monad', nativeSymbol: 'MON', logo: 'https://icons.llamao.fi/icons/chains/rsz_monad.jpg' },
] as const;

// Top stablecoins to check on non-Ink chains (Ink uses Blockscout for ALL tokens)
const STABLE_TOKENS = {
  USDC: {
    name: 'USD Coin',
    symbol: 'USDC',
    decimals: 6,
    addresses: {
      [mainnet.id]: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48' as Address,
      [base.id]: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as Address,
      [optimism.id]: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85' as Address,
      [arbitrum.id]: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831' as Address,
      [polygon.id]: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359' as Address,
      [avalanche.id]: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E' as Address,
    },
  },
  USDT: {
    name: 'Tether USD',
    symbol: 'USDT',
    decimals: 6,
    addresses: {
      [mainnet.id]: '0xdAC17F958D2ee523a2206206994597C13D831ec7' as Address,
      [base.id]: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2' as Address,
      [optimism.id]: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58' as Address,
      [arbitrum.id]: '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9' as Address,
      [polygon.id]: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F' as Address,
      [bsc.id]: '0x55d398326f99059fF775485246999027B3197955' as Address,
      [avalanche.id]: '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7' as Address,
    },
  },
  DAI: {
    name: 'Dai Stablecoin',
    symbol: 'DAI',
    decimals: 18,
    addresses: {
      [mainnet.id]: '0x6B175474E89094C44Da98b954EedeAC495271d0F' as Address,
      [base.id]: '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb' as Address,
      [optimism.id]: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1' as Address,
      [arbitrum.id]: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1' as Address,
      [polygon.id]: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063' as Address,
      [avalanche.id]: '0xd586E7F844cEa2F87f50152665BCbc2C279D8d70' as Address,
    },
  },
  WETH: {
    name: 'Wrapped Ether',
    symbol: 'WETH',
    decimals: 18,
    addresses: {
      [mainnet.id]: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2' as Address,
      [base.id]: '0x4200000000000000000000000000000000000006' as Address,
      [optimism.id]: '0x4200000000000000000000000000000000000006' as Address,
      [arbitrum.id]: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1' as Address,
      [polygon.id]: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619' as Address,
      [avalanche.id]: '0x49D5c2BdFfac6CE2BFdB6640F4F80f226bc10bAB' as Address,
    },
  },
  WBTC: {
    name: 'Wrapped Bitcoin',
    symbol: 'WBTC',
    decimals: 8,
    addresses: {
      [mainnet.id]: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599' as Address,
      [base.id]: '0x0555E30da8f98308EdB960aa94C0Db47230d2B9c' as Address,
      [optimism.id]: '0x68f180fcCe6836688e9084f035309E29Bf0A2095' as Address,
      [arbitrum.id]: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f' as Address,
      [polygon.id]: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6' as Address,
      [avalanche.id]: '0x50b7545627a5162F82A992c33b87aDc75187B218' as Address,
    },
  },
} as const;

const ERC20_ABI = parseAbi([
  'function balanceOf(address) view returns (uint256)',
]);

// Supabase tables for Mavrk tokens by chain
const MAVRK_TOKEN_TABLES: Record<number, string> = {
  [mainnet.id]: 'tokens_usd1', // USD1 launchpad tokens on Ethereum
  [monad.id]: 'tokens_monad',
};

// Cache for Mavrk tokens from Supabase
let mavrkTokenCache: Map<number, { tokens: Array<{ address: string; symbol: string; name: string; decimals: number; logo?: string }>; timestamp: number }> = new Map();
const MAVRK_TOKEN_CACHE_DURATION = 300000; // 5 minutes

// Fetch Mavrk tokens from Supabase for a specific chain
const fetchMavrkTokens = async (chainId: number): Promise<Array<{ address: string; symbol: string; name: string; decimals: number; logo?: string }>> => {
  const tableName = MAVRK_TOKEN_TABLES[chainId];
  if (!tableName) return [];

  // Check cache
  const cached = mavrkTokenCache.get(chainId);
  if (cached && Date.now() - cached.timestamp < MAVRK_TOKEN_CACHE_DURATION) {
    return cached.tokens;
  }

  try {
    // Note: tokens_monad doesn't have decimals column, so we select differently per table
    const selectColumns = tableName === 'tokens_eth' 
      ? 'contract_address, symbol, name, decimals, logo_url'
      : 'contract_address, symbol, name, logo_url'; // tokens_monad has no decimals
    
    const { data, error } = await supabase
      .from(tableName)
      .select(selectColumns)
      .not('contract_address', 'is', null)
      .limit(100);

    if (error || !data) {
      console.warn(`[MultiChainBalances] Failed to fetch Mavrk tokens from ${tableName}:`, error);
      return [];
    }

    const tokens = data.map((t: any) => ({
      address: t.contract_address,
      symbol: t.symbol || 'UNKNOWN',
      name: t.name || 'Unknown Token',
      decimals: t.decimals ?? 18, // Default to 18 if not present (tokens_monad case)
      logo: t.logo_url,
    }));

    // Cache the results
    mavrkTokenCache.set(chainId, { tokens, timestamp: Date.now() });
    console.log(`[MultiChainBalances] Fetched ${tokens.length} Mavrk tokens from ${tableName}`);

    return tokens;
  } catch (e) {
    console.warn(`[MultiChainBalances] Error fetching Mavrk tokens from ${tableName}:`, e);
    return [];
  }
};

// Cache for balances (30 seconds)
const CACHE_DURATION = 30000;
let balanceCache: Map<string, { data: TokenBalance[]; timestamp: number }> = new Map();

// Price cache to avoid repeated API calls (5 minutes)
const PRICE_CACHE_DURATION = 300000;
let priceCache: Map<string, { price: number | null; timestamp: number }> = new Map();

// Helper to fetch price from Relay API with caching
const fetchTokenPrice = async (address: string, chainId: number): Promise<number | null> => {
  const cacheKey = `${chainId}-${address.toLowerCase()}`;
  const cached = priceCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < PRICE_CACHE_DURATION) {
    return cached.price;
  }
  
  try {
    const response = await fetch(
      `https://api.relay.link/currencies/token/price?address=${address}&chainId=${chainId}&referrer=mavrk.ink`
    );
    if (!response.ok) {
      priceCache.set(cacheKey, { price: null, timestamp: Date.now() });
      return null;
    }
    const data = await response.json();
    const price = data.price || null;
    priceCache.set(cacheKey, { price, timestamp: Date.now() });
    return price;
  } catch (e) {
    console.warn(`Failed to fetch price for ${address} on ${chainId}`, e);
    priceCache.set(cacheKey, { price: null, timestamp: Date.now() });
    return null;
  }
};

// Batch fetch prices for multiple tokens
const fetchTokenPricesBatch = async (
  tokens: Array<{ address: string; chainId: number }>
): Promise<Map<string, number | null>> => {
  const results = new Map<string, number | null>();
  
  // Process in batches of 10 to avoid rate limiting
  const batchSize = 10;
  for (let i = 0; i < tokens.length; i += batchSize) {
    const batch = tokens.slice(i, i + batchSize);
    
    const promises = batch.map(async ({ address, chainId }) => {
      const price = await fetchTokenPrice(address, chainId);
      return { key: `${chainId}-${address.toLowerCase()}`, price };
    });
    
    const batchResults = await Promise.all(promises);
    batchResults.forEach(({ key, price }) => {
      results.set(key, price);
    });
    
    // Small delay between batches
    if (i + batchSize < tokens.length) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  return results;
};

export function useMultiChainBalances(enabled: boolean = true) {
  const { address } = useAccount();
  const config = useConfig();
  const [balances, setBalances] = useState<TokenBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedChains, setFailedChains] = useState<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!address || !enabled) {
      setBalances([]);
      setFailedChains([]);
      setLoading(false);
      return;
    }

    // Check cache
    const cacheKey = address.toLowerCase();
    const cached = balanceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log('[MultiChainBalances] Using cached balances');
      setBalances(cached.data);
      setFailedChains([]);
      setLoading(false);
      return;
    }

    // Cancel previous request
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const fetchBalances = async () => {
      setLoading(true);
      setError(null);
      setFailedChains([]);
      const allBalances: TokenBalance[] = [];
      const failures: string[] = [];

      try {
        console.log('[MultiChainBalances] Fetching balances for', address);

        // Fetch Relay currencies for logos
        let tokenMetadataMap = new Map<string, string>();
        try {
          const chainIds = SUPPORTED_CHAINS.map(c => c.chain.id);
          const currencies = await getRelayCurrencies(chainIds);
          currencies.forEach(c => {
            tokenMetadataMap.set(`${c.chainId}-${c.address.toLowerCase()}`, c.metadata?.logoURI || '');
          });
        } catch (e) {
          console.warn('[MultiChainBalances] Failed to fetch token metadata', e);
        }

        // Fetch balances for each chain in parallel
        const chainPromises = SUPPORTED_CHAINS.map(async ({ chain, name, nativeSymbol, logo: chainLogo }) => {
          const chainId = chain.id;
          try {
            const fetchChainData = async () => {
              console.log(`[MultiChainBalances] Starting fetch for ${name} (${chainId})`);
              const client = config.getClient({ chainId });
              if (!client) {
                console.warn(`[MultiChainBalances] No client for chain ${name}`);
                failures.push(name);
                return [];
              }

              const chainBalances: TokenBalance[] = [];

              // 1. Fetch native balance
              try {
                const nativeBalance = await getBalance(client, { address });
                if (nativeBalance > 0n) {
                  const price = await fetchTokenPrice('0x0000000000000000000000000000000000000000', chainId);
                  chainBalances.push({
                    chainId: chainId,
                    chainName: name,
                    tokenAddress: null,
                    symbol: nativeSymbol,
                    name: nativeSymbol,
                    balance: formatUnits(nativeBalance, 18),
                    balanceRaw: nativeBalance,
                    decimals: 18,
                    logo: chainLogo,
                    priceUSD: price || 0,
                    valueUSD: price ? price * parseFloat(formatUnits(nativeBalance, 18)) : 0,
                  });
                }
              } catch (nativeError) {
                console.warn(`[MultiChainBalances] Failed to fetch native balance for ${name}:`, nativeError);
              }

              // 2. For INK CHAIN - Use Blockscout to get ALL tokens in wallet
              if (chainId === ink.id) {
                try {
                  console.log('[MultiChainBalances] Fetching ALL Ink tokens via Blockscout...');
                  const inkTokens = await getAddressTokenBalances(address);
                  
                  console.log(`[MultiChainBalances] Found ${inkTokens.length} tokens on Ink`);
                  
                  // Filter tokens with non-zero balance
                  const tokensWithBalance = inkTokens.filter(t => t.value && BigInt(t.value) > 0n);
                  
                  // Limit to top 20 tokens for price fetching to avoid timeout
                  const tokensToPrice = tokensWithBalance
                    .slice(0, 20)
                    .map(t => ({
                      address: t.token.address || t.token.address_hash || '',
                      chainId: ink.id,
                    }))
                    .filter(t => t.address);
                  
                  // Batch fetch prices with a shorter timeout
                  let prices = new Map<string, number | null>();
                  try {
                    const pricePromise = fetchTokenPricesBatch(tokensToPrice);
                    const priceTimeout = new Promise<Map<string, number | null>>((resolve) =>
                      setTimeout(() => resolve(new Map()), 8000) // 8 second timeout for prices
                    );
                    prices = await Promise.race([pricePromise, priceTimeout]);
                  } catch (priceError) {
                    console.warn('[MultiChainBalances] Price fetching failed, continuing without prices');
                  }
                  
                  // Process each token (add all tokens, not just priced ones)
                  for (const tokenData of tokensWithBalance) {
                    const tokenAddress = (tokenData.token.address || tokenData.token.address_hash || '').toLowerCase();
                    if (!tokenAddress) continue;
                    
                    const balanceVal = BigInt(tokenData.value || '0');
                    if (balanceVal <= 0n) continue;
                    
                    const decimals = parseInt(tokenData.token.decimals || '18');
                    const balanceFormatted = formatUnits(balanceVal, decimals);
                    
                    // Get price from batch results (may be null)
                    const priceKey = `${ink.id}-${tokenAddress}`;
                    const price = prices.get(priceKey) || null;
                    
                    // Get logo from metadata or token data
                    let logo = tokenMetadataMap.get(priceKey);
                    if (!logo && tokenData.token.icon_url) {
                      logo = tokenData.token.icon_url;
                    }
                    
                    // Calculate USD value
                    const valueUSD = price ? price * parseFloat(balanceFormatted) : 0;
                    
                    chainBalances.push({
                      chainId: ink.id,
                      chainName: name,
                      tokenAddress: tokenAddress as Address,
                      symbol: tokenData.token.symbol || 'UNKNOWN',
                      name: tokenData.token.name || tokenData.token.symbol || 'Unknown Token',
                      balance: balanceFormatted,
                      balanceRaw: balanceVal,
                      decimals,
                      logo: logo || undefined,
                      priceUSD: price || 0,
                      valueUSD,
                    });
                  }
                  
                  console.log(`[MultiChainBalances] Processed ${chainBalances.length} Ink tokens with balances`);
                } catch (inkError) {
                  console.warn('[MultiChainBalances] Failed to fetch Ink tokens via Blockscout:', inkError);
                }
              } else {
                // 3. For OTHER CHAINS - Check predefined stablecoins via multicall
                const tokenCalls: Array<{ token: typeof STABLE_TOKENS[keyof typeof STABLE_TOKENS]; address: Address }> = [];

                Object.values(STABLE_TOKENS).forEach((token) => {
                  const tokenAddress = token.addresses[chainId as keyof typeof token.addresses];
                  if (tokenAddress) {
                    tokenCalls.push({ token, address: tokenAddress });
                  }
                });

                if (tokenCalls.length > 0) {
                  try {
                    const contracts = tokenCalls.map(({ address: tokenAddr }) => ({
                      address: tokenAddr,
                      abi: ERC20_ABI,
                      functionName: 'balanceOf' as const,
                      args: [address],
                    }));

                    const results = await multicall(client, { contracts });

                    const tokenPromises = results.map(async (result, index) => {
                      const { token, address: tokenAddress } = tokenCalls[index];
                      let balanceVal = result.status === 'success' ? (result.result as bigint) : 0n;

                      if (balanceVal > 0n) {
                        const balanceFormatted = formatUnits(balanceVal, token.decimals);
                        const price = await fetchTokenPrice(tokenAddress, chainId);
                        const logo = tokenMetadataMap.get(`${chainId}-${tokenAddress.toLowerCase()}`);

                        return {
                          chainId: chainId,
                          chainName: name,
                          tokenAddress,
                          symbol: token.symbol,
                          name: token.name,
                          balance: balanceFormatted,
                          balanceRaw: balanceVal,
                          decimals: token.decimals,
                          logo: logo || chainLogo,
                          priceUSD: price || 0,
                          valueUSD: price ? price * parseFloat(balanceFormatted) : 0,
                        } as TokenBalance;
                      }
                      return null;
                    });

                    const tokens = await Promise.all(tokenPromises);
                    chainBalances.push(...tokens.filter((t): t is TokenBalance => t !== null));
                  } catch (multicallError) {
                    console.warn(`[MultiChainBalances] Multicall failed for ${name}:`, multicallError);
                  }
                }

                // 4. For ETH and MONAD chains - Also check Mavrk tokens from Supabase
                if (chainId === mainnet.id || chainId === monad.id) {
                  try {
                    const mavrkTokens = await fetchMavrkTokens(chainId);
                    
                    if (mavrkTokens.length > 0) {
                      console.log(`[MultiChainBalances] Checking ${mavrkTokens.length} Mavrk tokens on ${name}`);
                      
                      // For Monad, use individual readContract calls since multicall3 is not supported
                      // For Ethereum, use multicall for efficiency
                      if (chainId === monad.id) {
                        // Individual calls for Monad
                        const mavrkPromises = mavrkTokens.map(async (token) => {
                          try {
                            const balanceVal = await readContract(client, {
                              address: token.address as Address,
                              abi: ERC20_ABI,
                              functionName: 'balanceOf',
                              args: [address],
                            }) as bigint;

                            if (balanceVal > 0n) {
                              const balanceFormatted = formatUnits(balanceVal, token.decimals);
                              const price = await fetchTokenPrice(token.address, chainId);
                              const logo = token.logo || tokenMetadataMap.get(`${chainId}-${token.address.toLowerCase()}`);

                              return {
                                chainId: chainId,
                                chainName: name,
                                tokenAddress: token.address as Address,
                                symbol: token.symbol,
                                name: token.name,
                                balance: balanceFormatted,
                                balanceRaw: balanceVal,
                                decimals: token.decimals,
                                logo: logo || chainLogo,
                                priceUSD: price || 0,
                                valueUSD: price ? price * parseFloat(balanceFormatted) : 0,
                              } as TokenBalance;
                            }
                          } catch (e) {
                            // Token contract might not exist or be inaccessible
                            console.debug(`[MultiChainBalances] Failed to check Monad token ${token.symbol}:`, e);
                          }
                          return null;
                        });

                        const mavrkBalances = await Promise.all(mavrkPromises);
                        const validMavrkBalances = mavrkBalances.filter((t): t is TokenBalance => t !== null);
                        
                        if (validMavrkBalances.length > 0) {
                          console.log(`[MultiChainBalances] Found ${validMavrkBalances.length} Mavrk tokens with balance on ${name}`);
                          chainBalances.push(...validMavrkBalances);
                        }
                      } else {
                        // Use multicall for Ethereum (more efficient)
                        const mavrkContracts = mavrkTokens.map(t => ({
                          address: t.address as Address,
                          abi: ERC20_ABI,
                          functionName: 'balanceOf' as const,
                          args: [address],
                        }));

                        const mavrkResults = await multicall(client, { contracts: mavrkContracts });

                        const mavrkPromises = mavrkResults.map(async (result, index) => {
                          const token = mavrkTokens[index];
                          let balanceVal = result.status === 'success' ? (result.result as bigint) : 0n;

                          if (balanceVal > 0n) {
                            const balanceFormatted = formatUnits(balanceVal, token.decimals);
                            const price = await fetchTokenPrice(token.address, chainId);
                            const logo = token.logo || tokenMetadataMap.get(`${chainId}-${token.address.toLowerCase()}`);

                            return {
                              chainId: chainId,
                              chainName: name,
                              tokenAddress: token.address as Address,
                              symbol: token.symbol,
                              name: token.name,
                              balance: balanceFormatted,
                              balanceRaw: balanceVal,
                              decimals: token.decimals,
                              logo: logo || chainLogo,
                              priceUSD: price || 0,
                              valueUSD: price ? price * parseFloat(balanceFormatted) : 0,
                            } as TokenBalance;
                          }
                          return null;
                        });

                        const mavrkBalances = await Promise.all(mavrkPromises);
                        const validMavrkBalances = mavrkBalances.filter((t): t is TokenBalance => t !== null);
                        
                        if (validMavrkBalances.length > 0) {
                          console.log(`[MultiChainBalances] Found ${validMavrkBalances.length} Mavrk tokens with balance on ${name}`);
                          chainBalances.push(...validMavrkBalances);
                        }
                      }
                    }
                  } catch (mavrkError) {
                    console.warn(`[MultiChainBalances] Failed to check Mavrk tokens on ${name}:`, mavrkError);
                  }
                }
              }

              console.log(`[MultiChainBalances] Finished fetch for ${name} (${chainId})`);
              return chainBalances;
            };

            // Race against a timeout (20 seconds for Ink due to more data)
            const timeoutMs = chainId === ink.id ? 20000 : 10000;
            const timeoutPromise = new Promise<TokenBalance[]>((_, reject) =>
              setTimeout(() => reject(new Error(`Timeout fetching ${name}`)), timeoutMs)
            );

            return await Promise.race([fetchChainData(), timeoutPromise]);

          } catch (chainError) {
            console.warn(`[MultiChainBalances] Failed to fetch balances for ${name}:`, chainError);
            failures.push(name);
            return [];
          }
        });

        const results = await Promise.all(chainPromises);
        results.forEach((chainBalances) => {
          allBalances.push(...chainBalances);
        });

        console.log(`[MultiChainBalances] Found ${allBalances.length} non-zero balances`);

        // Cache results
        balanceCache.set(cacheKey, {
          data: allBalances,
          timestamp: Date.now(),
        });

        setBalances(allBalances);
        setFailedChains(failures);
      } catch (err) {
        console.error('[MultiChainBalances] Error fetching balances:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch balances');
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [address, config, enabled]);

  return {
    balances,
    loading,
    error,
    failedChains,
    refetch: () => {
      if (address) {
        balanceCache.delete(address.toLowerCase());
        // Also clear price cache to get fresh prices
        priceCache.clear();
        setBalances([]);
        setFailedChains([]);
        setLoading(true);
      }
    },
  };
}

// Clear cache on demand
export function clearMultiChainBalanceCache() {
  balanceCache.clear();
  priceCache.clear();
  console.log('[MultiChainBalances] Cache cleared');
}
